/** Failures of the MCP authorize page's API calls, mapped to what the page shows.
 *
 * Status codes come from apps/api/src/routes/mcp-authorization.ts:
 *   401 {error:'Unauthorized'}         no portal session (route guard)
 *   503 {error:'service_unavailable'}  the Mission Control bridge failed
 *   409 {error:'live_transaction_exists'}
 *   400 {error:<McpRefusal>}           every other refusal (not_found, membership_inactive, ...)
 * 403 is not sent by that route today; it is treated like membership_inactive so a
 * future policy refusal still names the problem. */
export class McpAuthRequestError extends Error {
  constructor(public readonly status: number, public readonly code: string) {
    super(code);
    this.name = 'McpAuthRequestError';
  }
}

const CODE = /^[A-Za-z][A-Za-z0-9_.-]{0,63}$/;

/** Only a short identifier-shaped code is ever shown; anything else becomes http_<status>. */
export function safeErrorCode(raw: unknown, status: number): string {
  return typeof raw === 'string' && CODE.test(raw) ? raw : `http_${status}`;
}

export type McpAuthorizeFailure =
  | { kind: 'sign_in' }
  | { kind: 'no_operator_membership'; code: string }
  | { kind: 'service_unavailable'; code: string }
  | { kind: 'other'; code: string };

export function classifyMcpAuthorizeError(error: unknown): McpAuthorizeFailure {
  if (!(error instanceof McpAuthRequestError)) return { kind: 'other', code: 'request_failed' };
  if (error.status === 401 || error.code === 'unauthenticated') return { kind: 'sign_in' };
  if (error.status === 403 || error.code === 'membership_inactive') return { kind: 'no_operator_membership', code: error.code };
  if (error.status === 503 || error.code === 'service_unavailable') return { kind: 'service_unavailable', code: error.code };
  return { kind: 'other', code: error.code };
}

/** The sentence shown for a failure the page does not resolve by asking the user to sign in. */
export function mcpAuthorizeFailureMessage(failure: Exclude<McpAuthorizeFailure, { kind: 'sign_in' }>): string {
  switch (failure.kind) {
    case 'no_operator_membership':
      return 'The account you are signed in with has no active operator membership for this workspace. Switch to your operator account, then restart the connection from your MCP client.';
    case 'service_unavailable':
      return 'OCNexus could not reach the authorization service. Wait a minute, then restart the connection from your MCP client.';
    default:
      return 'Unable to verify access. Restart the connection from your MCP client.';
  }
}
